/**
 * App Settings page for application preferences
 * Displays and allows editing of video quality, language, and accessibility settings
 */
/* eslint-disable max-lines-per-function */

import { Bell, Monitor, Volume2 } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { apiCall } from "../../config/api";
import { Button } from "../../shared/components/ui/button";
import { Card } from "../../shared/components/ui/card";
import { Label } from "../../shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../shared/components/ui/select";
import { Switch } from "../../shared/components/ui/switch";
import { useToast } from "../../shared/hooks/use-toast";
import { LanguageMultiSelect } from "../admin/bulk-edit/components/LanguageMultiSelect";

type UserPreferences = {
  languages?: string[];
  subtitlesEnabled?: boolean;
  videoQuality?: string;
  autoplay?: boolean;
  notifications?: boolean;
  subtitleLanguages?: string[];
};

export function AppSettings(): ReactElement {
  const { toast } = useToast();
  const [autoplay, setAutoplay] = useState(true);
  const [subtitles, setSubtitles] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [videoQuality, setVideoQuality] = useState("auto");
  const [audioLanguages, setAudioLanguages] = useState<string[]>(["en"]);
  const [subtitleLanguages, setSubtitleLanguages] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await apiCall("/v1/user/profile");
        if (!response.ok) {
          throw new Error("Failed to load profile");
        }

        const data = (await response.json()) as { user: { preferences?: UserPreferences } };
        const prefs = data.user.preferences;

        if (prefs) {
          setAudioLanguages(prefs.languages ?? ["en"]);
          setSubtitles(prefs.subtitlesEnabled ?? false);
          setVideoQuality(prefs.videoQuality ?? "auto");
          setAutoplay(prefs.autoplay ?? true);
          setNotifications(prefs.notifications ?? true);
          setSubtitleLanguages(prefs.subtitleLanguages ?? []);
        }
      } catch (error) {
        console.error("Failed to load preferences:", error);
        toast({
          description: "Failed to load your settings",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    void loadPreferences();
  }, [toast]);

  const handleSaveSettings = async () => {
    setIsSaving(true);

    try {
      const response = await apiCall("/v1/user/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          preferences: {
            languages: audioLanguages,
            subtitlesEnabled: subtitles,
            videoQuality,
            autoplay,
            notifications,
            subtitleLanguages,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save settings");
      }

      toast({
        description: "Settings saved successfully",
        variant: "default",
      });
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast({
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-white mb-2">App Settings</h1>
        <p className="text-xs text-neutral-400 pb-6">Customize your viewing experience and app preferences</p>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-lumi-accent border-t-transparent rounded-full animate-spin" />
            <div className="text-neutral-400 text-sm">Loading settings...</div>
          </div>
        </div>
      ) : (
        <>
          {/* Playback Settings Card */}
          <Card className="bg-bg-transparent border-neutral-800 p-6">
            {/* ...rest of cards... */}
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-lumi-accent/10 rounded-lg">
                <Monitor className="h-5 w-5 text-lumi-accent" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Playback Settings</h2>
                <p className="text-sm text-neutral-400">Control video playback behavior</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label htmlFor="autoplay" className="text-base">
                    Autoplay Next Episode
                  </Label>
                  <p className="text-sm text-neutral-400 mt-1">Automatically play the next episode when one finishes</p>
                </div>
                <Switch id="autoplay" checked={autoplay} onCheckedChange={setAutoplay} />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-neutral-800">
                <div className="flex-1">
                  <Label htmlFor="subtitles" className="text-base">
                    Default Subtitles
                  </Label>
                  <p className="text-sm text-neutral-400 mt-1">Turn on subtitles by default for all content</p>
                </div>
                <Switch id="subtitles" checked={subtitles} onCheckedChange={setSubtitles} />
              </div>

              <div className="pt-4 border-t border-neutral-800">
                <Label htmlFor="quality" className="text-base mb-2 block">
                  Video Quality
                </Label>
                <p className="text-sm text-neutral-400 mb-3">
                  Choose default video quality (higher quality uses more data)
                </p>
                <Select value={videoQuality} onValueChange={setVideoQuality}>
                  <SelectTrigger className="w-full sm:w-64">
                    <span className="text-white">
                      {videoQuality === "auto" && "Auto (Recommended)"}
                      {videoQuality === "1080p" && "1080p (High)"}
                      {videoQuality === "720p" && "720p (Medium)"}
                      {videoQuality === "480p" && "480p (Low)"}
                    </span>
                  </SelectTrigger>
                  <SelectContent className="mt-1 w-full sm:w-64">
                    <SelectItem value="auto">Auto (Recommended)</SelectItem>
                    <SelectItem value="1080p">1080p (High)</SelectItem>
                    <SelectItem value="720p">720p (Medium)</SelectItem>
                    <SelectItem value="480p">480p (Low)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* Audio & Language Card */}
          <Card className="bg-bg-transparent border-neutral-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-lumi-accent/10 rounded-lg">
                <Volume2 className="h-5 w-5 text-lumi-accent" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Audio & Language</h2>
                <p className="text-sm text-neutral-400">Set your preferred audio and subtitle languages</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label htmlFor="audioLang" className="text-base mb-2 block">
                  Preferred Audio Languages
                </Label>
                <p className="text-sm text-neutral-400 mb-3">Select your preferred languages for audio tracks</p>
                <div className="w-full sm:w-96 px-3 py-2 border border-neutral-700 rounded-lg">
                  <LanguageMultiSelect
                    value={audioLanguages}
                    onChange={setAudioLanguages}
                    containerClassName="flex items-center justify-between gap-2 w-full"
                    comboboxTriggerClassName="w-6 h-6 p-0 border-none"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="subtitleLang" className="text-base mb-2 block">
                  Preferred Subtitle Languages
                </Label>
                <p className="text-sm text-neutral-400 mb-3">Select your preferred languages for subtitles</p>
                <div className="w-full sm:w-96 px-3 py-2 border border-neutral-700 rounded-lg">
                  <LanguageMultiSelect
                    value={subtitleLanguages}
                    onChange={setSubtitleLanguages}
                    containerClassName="flex items-center justify-between gap-2 w-full"
                    comboboxTriggerClassName="w-6 h-6 p-0 border-none"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Notifications Card */}
          <Card className="bg-bg-transparent border-neutral-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-lumi-accent/10 rounded-lg">
                <Bell className="h-5 w-5 text-lumi-accent" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Notifications</h2>
                <p className="text-sm text-neutral-400">Manage how you receive updates</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label htmlFor="notifications" className="text-base">
                    Email Notifications
                  </Label>
                  <p className="text-sm text-neutral-400 mt-1">Receive updates about new content and features</p>
                </div>
                <Switch id="notifications" checked={notifications} onCheckedChange={setNotifications} />
              </div>
            </div>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={() => void handleSaveSettings()} disabled={isSaving} variant="outline">
              {isSaving ? "Saving..." : "Save All Settings"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
